import { useMemo, useState } from "react";
import {
  CloudDownloadIcon,
  InformationCircleIcon,
  PhotographIcon,
} from "@heroicons/react/outline";
import ImageViewer from "react-simple-image-viewer";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as imageUtils from "../../imageUtils";

function normalizePhoto(photo) {
  const image = imageUtils.normalizeCloudinaryUrl(photo.image);
  return {
    ...photo,
    image,
    thumbnail: photo.thumbnail || image,
    preview: photo.preview || image,
  };
}

function photoCategory(photo) {
  if (photo.customer_uploaded) return "Customer provided";
  return photo.interior ? "Interior" : "Exterior";
}

function uniqueFilename(photo, usedNames) {
  const original = photo.name || `closeout-photo-${photo.id || usedNames.size + 1}`;
  if (!usedNames.has(original)) {
    usedNames.add(original);
    return original;
  }

  const extensionIndex = original.lastIndexOf(".");
  const base = extensionIndex > 0 ? original.slice(0, extensionIndex) : original;
  const extension = extensionIndex > 0 ? original.slice(extensionIndex) : "";
  const filename = `${base}-${photo.id || usedNames.size + 1}${extension}`;
  usedNames.add(filename);
  return filename;
}

export default function SharedJobPhotoListing({ photos = [], purchase_order }) {
  const [viewerIndex, setViewerIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const normalizedPhotos = useMemo(
    () => photos.map(normalizePhoto).filter((photo) => photo.image),
    [photos],
  );
  const groups = useMemo(() => {
    const categories = ["Customer provided", "Interior", "Exterior"];
    return categories
      .map((name) => ({
        name,
        photos: normalizedPhotos.filter(
          (photo) => photoCategory(photo) === name,
        ),
      }))
      .filter((group) => group.photos.length > 0);
  }, [normalizedPhotos]);

  const openViewer = (photo) => {
    const index = normalizedPhotos.findIndex((item) => item.id === photo.id);
    setViewerIndex(index >= 0 ? index : 0);
    setIsViewerOpen(true);
  };

  const downloadAllPhotos = async () => {
    if (normalizedPhotos.length === 0 || downloading) return;

    setDownloading(true);
    try {
      const zip = new JSZip();
      const usedNames = new Set();
      await Promise.all(
        normalizedPhotos.map(async (photo) => {
          const response = await fetch(photo.image);
          if (!response.ok) throw new Error("Unable to download photo");
          zip.file(uniqueFilename(photo, usedNames), await response.blob());
        }),
      );
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(
        content,
        `${purchase_order || "job-closeout"}_all_photos.zip`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const categorySummary = groups
    .map((group) => `${group.name} · ${group.photos.length}`)
    .join(" · ");
  const hasInterior = groups.some((group) => group.name === "Interior");
  const hasExterior = groups.some((group) => group.name === "Exterior");

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
            <PhotographIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-extrabold text-slate-950 sm:text-base">
              Closeout photos
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {normalizedPhotos.length > 0
                ? `${categorySummary} photo${
                    normalizedPhotos.length === 1 ? "" : "s"
                  } provided as service evidence`
                : "Photo evidence shared with this closeout"}
            </p>
          </div>
        </div>
        {normalizedPhotos.length > 0 && (
          <button
            type="button"
            onClick={downloadAllPhotos}
            disabled={downloading}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
          >
            <CloudDownloadIcon
              className={`h-4 w-4 ${downloading ? "animate-pulse" : ""}`}
              aria-hidden="true"
            />
            {downloading ? "Preparing…" : "Download"}
          </button>
        )}
      </div>

      {normalizedPhotos.length === 0 ? (
        <div className="grid min-h-[150px] place-items-center p-6 text-center">
          <div>
            <PhotographIcon className="mx-auto h-9 w-9 text-slate-300" aria-hidden="true" />
            <p className="mt-3 text-sm font-bold text-slate-700">
              No closeout photos
            </p>
            <p className="mt-1 text-xs text-slate-500">
              No photo evidence was included with this closeout.
            </p>
          </div>
        </div>
      ) : (
        <div className="p-4 sm:p-5">
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2.5 text-xs leading-5 text-sky-800">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              Only categories containing closeout photos are shown.
              {!hasInterior && hasExterior &&
                " No interior photos were included."}
              {hasInterior && !hasExterior &&
                " No exterior photos were included."}
            </p>
          </div>

          <div className="space-y-5">
            {groups.map((group) => (
              <div key={group.name}>
                {groups.length > 1 && (
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                    {group.name}
                  </h3>
                )}
                <div
                  data-testid={`closeout-photo-grid-${group.name
                    .toLowerCase()
                    .replaceAll(" ", "-")}`}
                  className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
                >
                  {group.photos.map((photo, index) => (
                    <button
                      key={photo.id || `${group.name}-${index}`}
                      type="button"
                      onClick={() => openViewer(photo)}
                      className="overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-sm transition hover:border-slate-300 hover:shadow"
                      aria-label={`${group.name} closeout photo ${index + 1}`}
                    >
                      <img
                        src={photo.thumbnail}
                        alt={`${group.name} closeout ${index + 1}`}
                        className="aspect-[4/3] w-full bg-slate-100 object-cover"
                      />
                      <span className="flex items-center justify-between gap-2 px-2.5 py-2 text-[11px]">
                        <span className="font-semibold text-slate-600">
                          {group.name}
                        </span>
                        <span className="text-slate-400">
                          {index + 1} of {group.photos.length}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isViewerOpen && (
        <ImageViewer
          src={normalizedPhotos.map((photo) => photo.preview || photo.image)}
          currentIndex={viewerIndex}
          disableScroll={false}
          closeOnClickOutside={true}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </section>
  );
}
